import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, OperatorFunction, catchError, map, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

import { PaymentQrRepository } from '@app/domain/ports/payment-qr.repository';
import type {
  PaymentQr,
  PaymentQrCreateInput,
  PaymentQrListFilter,
  PaymentQrPage,
} from '@app/domain/models/payments-qr/payment-qr.model';
import { PaymentQrError } from '@app/domain/models/payments-qr/payment-qr.error';
import type { PaymentQrDto, PaymentQrListDto } from '@app/data/dtos/payments/payment-qr.dto';
import {
  toCreateQrRequestDto,
  toPaymentQr,
  toPaymentQrError,
  toPaymentQrPage,
} from '@app/data/mappers/payments/payment-qr.mapper';

/** Tope del contrato para `limit` (default del back: 50). */
const QR_LIST_MAX_LIMIT = 200;

/**
 * `?` de `GET qr/`: cada filtro SOLO si aplica. `limit` acotado a 1..200 y
 * `offset` solo si es un entero > 0 (la primera página no lo manda).
 */
export function buildQrListParams(filter: PaymentQrListFilter): HttpParams {
  let params = new HttpParams();
  const put = (key: string, value: string | number | undefined | null): void => {
    if (value === undefined || value === null) return;
    const text = String(value);
    if (text !== '') params = params.set(key, text);
  };
  put('id_company', filter.idCompany);
  put('status', filter.status);
  put('date_from', filter.dateFrom);
  put('date_to', filter.dateTo);
  put('min_amount', filter.minAmount);
  put('max_amount', filter.maxAmount);
  put('search', filter.search?.trim());
  if (typeof filter.limit === 'number' && Number.isFinite(filter.limit)) {
    put('limit', Math.min(QR_LIST_MAX_LIMIT, Math.max(1, Math.trunc(filter.limit))));
  }
  if (typeof filter.offset === 'number' && Number.isInteger(filter.offset) && filter.offset > 0) {
    put('offset', filter.offset);
  }
  return params;
}

/**
 * Cobro QR sobre HTTP (`${apiUrl}/api/v1/payments/qr/`).
 *
 * NO fija `Authorization` ni toca `sessionStorage`/`EncryptionService`: el
 * token lo pone el interceptor global (la deuda de hacerlo a mano en
 * `PolipayPaymentsRemoteDataSource` no se replica). `/api/v1/payments/` ya
 * está excluido del modal de error global, así que cada error llega aquí y
 * sale convertido en `PaymentQrError` — sin cuerpo del backend.
 */
@Injectable()
export class PaymentQrRemoteDataSource extends PaymentQrRepository {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/api/v1/payments/qr`;

  create(input: PaymentQrCreateInput, idempotencyKey: string): Observable<PaymentQr> {
    const headers = new HttpHeaders({ 'Idempotency-Key': idempotencyKey });
    return this.http
      .post<PaymentQrDto>(`${this.baseUrl}/`, toCreateQrRequestDto(input), { headers })
      .pipe(map(toPaymentQr), this.toDomainError());
  }

  list(filter: PaymentQrListFilter): Observable<PaymentQrPage> {
    return this.http
      .get<PaymentQrListDto>(`${this.baseUrl}/`, { params: buildQrListParams(filter) })
      .pipe(map(toPaymentQrPage), this.toDomainError());
  }

  getById(idQr: number): Observable<PaymentQr> {
    return this.http
      .get<PaymentQrDto>(`${this.baseUrl}/${encodeURIComponent(String(idQr))}/`)
      .pipe(map(toPaymentQr), this.toDomainError());
  }

  cancel(idQr: number): Observable<PaymentQr> {
    return this.http
      .post<PaymentQrDto>(`${this.baseUrl}/${encodeURIComponent(String(idQr))}/cancel/`, {})
      .pipe(map(toPaymentQr), this.toDomainError());
  }

  private toDomainError<T>(): OperatorFunction<T, T> {
    return catchError((err: unknown) =>
      throwError(() =>
        err instanceof HttpErrorResponse
          ? toPaymentQrError(err.status, err.error)
          // Una falla que no es HTTP (p. ej. un error de programación) no es
          // "sin red": se clasifica como desconocida.
          : new PaymentQrError('unknown', 0),
      ),
    );
  }
}
